import React, { useEffect, useState, useMemo } from "react";
import {
    Activity, Bell, Heart, AlertTriangle, Pill, TrendingUp,
    Clock, CheckCircle2, XCircle, MinusCircle, ArrowUpRight, Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    LineChart, Line, Legend,
} from "recharts";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const COLORS = {
    taken: "#10b981",
    missed: "#ef4444",
    skipped: "#f59e0b",
    pending: "#94a3b8",
    snoozed: "#6366f1",
    delayed: "#8b5cf6",
};

const StatCard = ({ icon: Icon, label, value, hint, accent = "brand", testId }) => {
    const palette = {
        brand: "from-brand-600 to-brand-400 text-brand-700 bg-brand-50",
        emerald: "from-emerald-600 to-emerald-400 text-emerald-700 bg-emerald-50",
        rose: "from-rose-600 to-rose-400 text-rose-700 bg-rose-50",
        amber: "from-amber-600 to-amber-400 text-amber-700 bg-amber-50",
        slate: "from-slate-700 to-slate-500 text-slate-700 bg-slate-100",
    };
    const cls = palette[accent] || palette.brand;
    return (
        <div
            className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-soft transition-all hover:shadow-md"
            data-testid={testId}
        >
            <div className={`absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${cls.split(" ").slice(0, 2).join(" ")} opacity-10`} />
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums">{value}</p>
                    {hint && <p className="mt-1 line-clamp-1 text-xs text-slate-500">{hint}</p>}
                </div>
                <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${cls.split(" ").slice(0, 2).join(" ")} text-white shadow-sm`}>
                    <Icon className="h-4 w-4" />
                </span>
            </div>
        </div>
    );
};

const formatHour = (h) => {
    const suffix = h >= 12 ? "PM" : "AM";
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${display}${suffix}`;
};

const Content = () => {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            try {
                const { data: d } = await api.get("/admin/reminders/analytics", { params: { days } });
                if (mounted) setData(d);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [days]);

    const statusPieData = useMemo(() => {
        if (!data) return [];
        const { taken, missed, skipped, pending } = data.summary;
        return [
            { name: "Taken", value: taken, color: COLORS.taken },
            { name: "Missed", value: missed, color: COLORS.missed },
            { name: "Skipped", value: skipped, color: COLORS.skipped },
            { name: "Pending", value: pending, color: COLORS.pending },
        ].filter((d) => d.value > 0);
    }, [data]);

    if (loading || !data) {
        return (
            <div className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 skeleton rounded-2xl" />)}
                </div>
                <div className="h-72 skeleton rounded-2xl" />
                <div className="h-64 skeleton rounded-2xl" />
            </div>
        );
    }

    const { summary, notifications, hourly, top_medications, daily_trend, recent_missed } = data;

    return (
        <div className="space-y-8" data-testid="admin-reminders-analytics">
            {/* Window selector */}
            <header className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">PILLS Reminder OS</p>
                    <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Customer adherence pulse</h2>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-1">
                    {[7, 30, 90].map((d) => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                                days === d ? "bg-brand-600 text-white" : "text-slate-600 hover:bg-slate-50"
                            }`}
                            data-testid={`window-${d}d`}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </header>

            {/* KPI cards */}
            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    icon={Heart}
                    label="Adherence"
                    value={summary.adherence_pct != null ? `${summary.adherence_pct}%` : "—"}
                    hint={`${summary.taken} taken / ${summary.taken + summary.missed + summary.skipped} settled`}
                    accent={summary.adherence_pct == null ? "slate" : summary.adherence_pct >= 80 ? "emerald" : summary.adherence_pct >= 60 ? "amber" : "rose"}
                    testId="kpi-adherence"
                />
                <StatCard
                    icon={AlertTriangle}
                    label="Critical adherence"
                    value={summary.critical_adherence_pct != null ? `${summary.critical_adherence_pct}%` : "—"}
                    hint={`${summary.critical_medications} critical meds tracked`}
                    accent={summary.critical_adherence_pct == null ? "slate" : summary.critical_adherence_pct >= 90 ? "emerald" : summary.critical_adherence_pct >= 75 ? "amber" : "rose"}
                    testId="kpi-critical-adherence"
                />
                <StatCard
                    icon={Pill}
                    label="Active medications"
                    value={summary.active_medications.toLocaleString("en-IN")}
                    hint={`${summary.users_with_meds} customers using PILLS Reminder`}
                    accent="brand"
                    testId="kpi-active-meds"
                />
                <StatCard
                    icon={Bell}
                    label="Reminders sent"
                    value={notifications.total_pushes.toLocaleString("en-IN")}
                    hint={`Across ${summary.total_doses_in_window} doses`}
                    accent="amber"
                    testId="kpi-reminders-sent"
                />
            </section>

            {/* Status pie + Notification escalation */}
            <section className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="dose-outcomes-chart">
                    <header className="mb-3">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Dose outcomes</p>
                        <h3 className="text-sm font-semibold text-slate-900">Status distribution — last {days}d</h3>
                    </header>
                    {statusPieData.length === 0 ? (
                        <p className="py-8 text-center text-sm text-slate-400">No doses in this window.</p>
                    ) : (
                        <div className="grid grid-cols-[1fr_140px] items-center gap-4">
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={statusPieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={88} paddingAngle={2}>
                                        {statusPieData.map((s) => <Cell key={s.name} fill={s.color} />)}
                                    </Pie>
                                    <Tooltip
                                        formatter={(v) => [v.toLocaleString("en-IN"), "Doses"]}
                                        contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <ul className="space-y-2 text-xs">
                                {statusPieData.map((s) => (
                                    <li key={s.name} className="flex items-center gap-2">
                                        <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                                        <span className="flex-1 text-slate-600">{s.name}</span>
                                        <span className="font-semibold tabular-nums text-slate-900">{s.value}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="notification-escalation-card">
                    <header className="mb-4">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Notification escalation</p>
                        <h3 className="text-sm font-semibold text-slate-900">Reminder funnel — last {days}d</h3>
                    </header>
                    <div className="space-y-3">
                        {[
                            { key: "reminder_1", label: "First reminder", color: "bg-brand-500" },
                            { key: "reminder_2", label: "Second nudge", color: "bg-amber-500" },
                            { key: "reminder_3", label: "Final escalation", color: "bg-rose-500" },
                        ].map((row) => {
                            const pct = notifications.reminder_1 ? (notifications[row.key] / notifications.reminder_1) * 100 : 0;
                            return (
                                <div key={row.key} data-testid={`notification-${row.key}`}>
                                    <div className="mb-1 flex items-center justify-between text-xs">
                                        <span className="font-medium text-slate-600">{row.label}</span>
                                        <span className="tabular-nums font-semibold text-slate-900">{notifications[row.key]}</span>
                                    </div>
                                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                                        <div className={`h-full ${row.color}`} style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] text-slate-500">
                        Drop-off across the 3-step nudge funnel. Lower bars on reminder 3 = customers acted before escalation.
                    </p>
                </div>
            </section>

            {/* Hourly distribution */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="hourly-distribution-card">
                <header className="mb-3">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">When customers dose</p>
                    <h3 className="text-sm font-semibold text-slate-900">Scheduled doses by hour (IST)</h3>
                </header>
                <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={hourly} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="hour" tickFormatter={formatHour} fontSize={10} stroke="#94a3b8" tickLine={false} axisLine={false} />
                        <YAxis fontSize={10} stroke="#94a3b8" tickLine={false} axisLine={false} />
                        <Tooltip
                            cursor={{ fill: "rgba(109, 40, 217, 0.05)" }}
                            labelFormatter={(h) => `${formatHour(h)}`}
                            formatter={(v) => [v, "Doses"]}
                            contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                        />
                        <Bar dataKey="doses" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </section>

            {/* Daily trend */}
            {daily_trend.length > 1 && (
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="daily-trend-card">
                    <header className="mb-3 flex items-center justify-between">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Daily trend</p>
                            <h3 className="text-sm font-semibold text-slate-900">Taken vs missed — {daily_trend.length}-day series</h3>
                        </div>
                        <TrendingUp className="h-4 w-4 text-slate-400" />
                    </header>
                    <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={daily_trend} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="date" fontSize={10} stroke="#94a3b8" tickLine={false} axisLine={false} tickFormatter={(d) => d.slice(5)} />
                            <YAxis fontSize={10} stroke="#94a3b8" tickLine={false} axisLine={false} />
                            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Line type="monotone" dataKey="taken" stroke={COLORS.taken} strokeWidth={2} dot={false} name="Taken" />
                            <Line type="monotone" dataKey="missed" stroke={COLORS.missed} strokeWidth={2} dot={false} name="Missed" />
                            <Line type="monotone" dataKey="skipped" stroke={COLORS.skipped} strokeWidth={2} dot={false} name="Skipped" />
                        </LineChart>
                    </ResponsiveContainer>
                </section>
            )}

            {/* Top medications + Recent missed */}
            <section className="grid gap-5 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white shadow-soft" data-testid="top-medications-card">
                    <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Top medications</p>
                            <h3 className="text-sm font-semibold text-slate-900">Most-tracked drugs</h3>
                        </div>
                        <Pill className="h-4 w-4 text-slate-400" />
                    </header>
                    {top_medications.length === 0 ? (
                        <p className="px-5 py-10 text-center text-sm text-slate-400">No medications tracked yet.</p>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {top_medications.map((m, i) => (
                                <li key={m.medication_id || i} className="flex items-center gap-3 px-5 py-3">
                                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[11px] font-semibold text-brand-700">
                                        {i + 1}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="line-clamp-1 text-sm font-medium text-slate-900">{m.medicine_name}</p>
                                        <p className="text-[11px] text-slate-500">
                                            {m.doses} doses · {m.taken} taken · {m.missed} missed
                                            {m.is_critical && <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-rose-50 px-1 text-[9px] font-semibold text-rose-700"><AlertTriangle className="h-2.5 w-2.5" />Critical</span>}
                                        </p>
                                    </div>
                                    {m.adherence_pct != null && (
                                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ${
                                            m.adherence_pct >= 80 ? "bg-emerald-100 text-emerald-700" :
                                            m.adherence_pct >= 60 ? "bg-amber-100 text-amber-700" :
                                            "bg-rose-100 text-rose-700"
                                        }`}>
                                            {m.adherence_pct}%
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-soft" data-testid="recent-missed-card">
                    <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Recently missed</p>
                            <h3 className="text-sm font-semibold text-slate-900">Latest 20 missed doses</h3>
                        </div>
                        <XCircle className="h-4 w-4 text-rose-400" />
                    </header>
                    {recent_missed.length === 0 ? (
                        <div className="px-5 py-10 text-center text-sm text-slate-400">
                            <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-emerald-300" />
                            No missed doses recorded — adherence is healthy 🎉
                        </div>
                    ) : (
                        <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
                            {recent_missed.map((r) => (
                                <li key={r.dose_id} className="flex items-center gap-3 px-5 py-3 text-xs">
                                    <Clock className="h-3.5 w-3.5 shrink-0 text-rose-400" />
                                    <div className="min-w-0 flex-1">
                                        <p className="line-clamp-1 font-medium text-slate-900">
                                            {r.medicine_name}
                                            {r.is_critical && <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-rose-50 px-1 text-[9px] font-semibold text-rose-700"><AlertTriangle className="h-2.5 w-2.5" />Critical</span>}
                                        </p>
                                        <p className="line-clamp-1 text-[11px] text-slate-500">
                                            {r.user_name} · {r.slot_label || "Custom"} · {new Date(r.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                                        </p>
                                    </div>
                                    <Link to={`/admin/users/${r.user_id}`} className="shrink-0 text-[11px] font-medium text-brand-700 hover:underline">
                                        View →
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>

            <footer className="flex items-center gap-2 text-[11px] text-slate-400">
                <Sparkles className="h-3 w-3 text-brand-500" />
                v3F • Reminder analytics computes on every request — no caching.
            </footer>
        </div>
    );
};

const AdminRemindersAnalytics = () => (
    <AdminAuthGuard>
        <AdminLayout title="Reminders Analytics" subtitle="PILLS Reminder OS adherence & engagement pulse">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminRemindersAnalytics;
